import posthog from 'posthog-js';

// Detect if we're in CI environment
const isCI = () => {
	return !!process.env.CI;
};

// No-op functions for CI environment
const noOpPostHog = {
	capture: () => undefined,
	isFeatureEnabled: () => false,
	getFeatureFlag: () => undefined,
	get_distinct_id: () => 'ci-test-id',
	get_session_id: () => 'ci-session-id',
	setPersonProperties: () => undefined,
	group: () => undefined,
	register: () => undefined,
	debug: () => undefined,
	onFeatureFlags: (callback: (...args: unknown[]) => void) => callback(),
	reloadFeatureFlags: () => undefined,
	init: () => undefined,
	startSessionRecording: () => undefined,
	isNoOp: () => true,
};

// Type-safe wrapper that matches PostHog interface subset we use
export interface PostHogWrapper {
	capture: (event: string, properties?: Record<string, unknown>) => void;
	isFeatureEnabled: (flag: string) => boolean | undefined;
	getFeatureFlag: (flag: string) => string | boolean | undefined;
	get_distinct_id: () => string;
	get_session_id: () => string;
	setPersonProperties: (properties: Record<string, unknown>) => void;
	group: (groupType: string, groupKey: string, properties?: Record<string, unknown>) => void;
	register: (properties: Record<string, unknown>) => void;
	debug: () => void;
	onFeatureFlags: (callback: (...args: unknown[]) => void) => void;
	reloadFeatureFlags: () => void;
	init: (apiKey: string, options?: Record<string, unknown>) => void;
	startSessionRecording: () => void;
	isNoOp?: () => boolean;
}

// Create wrapper that normalizes PostHog behavior
const createPostHogWrapper = (): PostHogWrapper => {
	if (isCI()) {
		return noOpPostHog;
	}

	// Wrap real PostHog to ensure consistent return types
	return {
		capture: posthog.capture.bind(posthog),
		isFeatureEnabled: posthog.isFeatureEnabled.bind(posthog),
		getFeatureFlag: posthog.getFeatureFlag.bind(posthog),
		get_distinct_id: posthog.get_distinct_id.bind(posthog),
		get_session_id: posthog.get_session_id.bind(posthog),
		setPersonProperties: posthog.setPersonProperties.bind(posthog),
		group: posthog.group.bind(posthog),
		register: posthog.register.bind(posthog),
		debug: posthog.debug.bind(posthog),
		onFeatureFlags: posthog.onFeatureFlags.bind(posthog),
		reloadFeatureFlags: posthog.reloadFeatureFlags.bind(posthog),
		init: posthog.init.bind(posthog),
		startSessionRecording: posthog.startSessionRecording.bind(posthog),
	};
};

// Export PostHog instance
const posthogClient: PostHogWrapper = createPostHogWrapper();

export default posthogClient;
